import { createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { compareNum } from "../../../app/arrays";
import { RootState } from "../../../app/store";
import { ImportedDocument, ImportedDocumentBlockSuggestions, ImportedDocumentElement, loadImportedDocumentById, loadImportedDocumentSuggestions } from "./importedDocumentsApi";
import { DocumentActions, DocumentContentActions, DocumentWorkflowActions, loadDocumentActions } from "../documentsApi";

const adapter = createEntityAdapter<ImportedDocumentElement>({
    selectId: ch => ch.id,
    sortComparer: (a, b) => {
        const dPage = compareNum(a.pageNumber ?? 0, b.pageNumber ?? 0)
        if (dPage === 0) return compareNum(a.order, b.order)
        else return dPage
    },
})

interface ImportedDocumentState {
    doc: ImportedDocument | null
    elements: EntityState<ImportedDocumentElement>
    suggestions: ImportedDocumentBlockSuggestions[]
    actions: DocumentContentActions[]
    workflowActions: DocumentWorkflowActions[]
}

const initialState: ImportedDocumentState = {
    doc: null,
    elements: adapter.getInitialState(),
    suggestions: [],
    actions: [],
    workflowActions: [],
}

export const importedDocumentSlice = createSlice({
    name: 'importedDocument',
    initialState,
    reducers: {
        updateImportedDocumentHeader(state, action: { payload: Partial<ImportedDocument> }) {
            if (state.doc) {
                state.doc = { ...state.doc, ...action.payload }
            }
        },
        // Immediate update after a workflow action returns the recomputed action sets,
        // ahead of the follow-up reload.
        setImportedDocumentActions(state, action: PayloadAction<DocumentActions>) {
            state.actions = action.payload.documentContentActions
            state.workflowActions = action.payload.workflowActions
        },
    },
    extraReducers: builder => {
        builder.addCase(loadImportedDocumentById.fulfilled, (state, action) => {
            state.doc = action.payload
            adapter.setAll(state.elements, action.payload.elements)
            state.suggestions = []
            // Clear until loadDocumentActions resolves so the previous document's
            // actions can't flash on the freshly loaded one.
            state.actions = []
            state.workflowActions = []
        })
        builder.addCase(loadImportedDocumentSuggestions.fulfilled, (state, action) => {
            state.suggestions = action.payload
        })
        builder.addCase(loadDocumentActions.fulfilled, (state, action) => {
            state.actions = action.payload.documentContentActions
            state.workflowActions = action.payload.workflowActions
        })
    },
})

export const { updateImportedDocumentHeader, setImportedDocumentActions } = importedDocumentSlice.actions

export const selectImportedDocument = (state: RootState) => state.importedDocument

export const selectImportedDocActions = (state: RootState) => selectImportedDocument(state).actions

export const selectImportedDocWorkflowActions = (state: RootState) => selectImportedDocument(state).workflowActions

export const selectImportedDocumentSuggestions = (state: RootState) => selectImportedDocument(state).suggestions

export const selectImportedDocumentSuggestionsForBlock = (state: RootState, blockKey: string) =>
    selectImportedDocumentSuggestions(state).find(s => s.targetBlockKey.toLowerCase() === blockKey.toLowerCase())

export const {
    selectAll: selectAllElements,
    selectById: selectElementById,
} = adapter.getSelectors((state: RootState) => selectImportedDocument(state).elements)

export default importedDocumentSlice.reducer
